import { Params } from '@angular/router';
import {
  isoWeekMondayDate,
  NOTIFICATION_TARGET_PARAM,
  parseIsoWeek,
  parseNotificationDate,
  parseNotificationId,
} from 'src/app/core/services/notification/deep-link/notification-target';

/**
 * Ziele der Glocken-/Push-Meldungen außerhalb der Aufgaben (P4, 17.10.2026).
 *
 * Der Server schickt rollenneutrale Kurzpfade (`NavigateTo`); er kennt weder Rolle noch Gerät des
 * Empfängers. Welche Fläche aufgeht, entscheidet diese Funktion — dasselbe Muster wie
 * `modules/tasks/nav/task-deep-link.ts`:
 *
 * | Kurzpfad | Mitarbeiter Telefon | Mitarbeiter Desktop | Leitung |
 * |---|---|---|---|
 * | `/absences/open/:id` | `tab-absences?absence=` | `/employee/dashboard/abwesenheit?absence=` | wie Desktop (eigene Anträge) |
 * | `/time/corrections/open/:id[?date=]` | `tab-time-overview?correction=[&date=]` | `/employee/dashboard/zeiten?correction=[&date=]` | wie Desktop |
 * | `/shifts/swaps/open/:id` | `tab-today?swap=` | `/employee/dashboard/heute?swap=` | `/planner?swapApprovals=` ¹ |
 * | `/shifts/week/:isoWeek` | `tab-my-shifts?week=` | `/employee/dashboard/schichten?week=` | `/planner?date=<Montag>` |
 * | `/shifts/open-shifts` | `tab-today` | `/employee/dashboard/heute` | `/planner` |
 * | `/absences/review/:id` | `tab-absences?absence=` | `/employee/dashboard/abwesenheit?absence=` | `/absence?absenceReview=` ² |
 * | `/time/corrections/review/:id` | `tab-time-overview?correction=` | `/employee/dashboard/zeiten?correction=` | `/dashboard?correctionReview=` ³ |
 * | `/employees/change-requests/open/:id` | `/employee-mobile/profile/change-requests?changeRequest=` | `/employee/dashboard/profil/change-requests?changeRequest=` | `/employee?changeRequestReview=` ⁴ |
 *
 * ¹ Der Planer öffnet daraus das Pop-up „Tauschgenehmigungen“, wenn der Tausch auf die Entscheidung der
 * Leitung wartet. Ist sie selbst Tauschpartner, schon entschieden oder darf nicht genehmigen (Teamleitung,
 * Recht/Abo/Firmenschalter fehlt), landet sie auf `/employee/dashboard/heute?swap=` — dem eigenen Tausch
 * (`modules/swap-shift/util/swap-notification-target.ts`).
 * ² Die Abwesenheitsseite öffnet den Genehmigungsdialog (`AcceptAbsenceComponent`), bei einer
 * Berufsschul-Serie für alle offenen Tage. Schon entschieden/gelöscht → Hinweis; ohne
 * `CanApproveAbsences` → Seite ohne Dialog + Hinweis (`modules/absence/util/absence-review-target.ts`).
 * ³ Das Dashboard öffnet den Entscheidungsdialog (`time-recording/component/time-correction-review-dialog/`,
 * seit 18.10.2026); Daten aus der Kachel „Genehmigungen & Hinweise“ (`modules/dashboard/util/correction-review-target.ts`).
 * ⁴ Die Mitarbeiterliste öffnet den Änderungsantrag (`ChangeRequestDetailDialogComponent`). Ist es
 * der eigene Antrag der Leitung (Genehmigt/Abgelehnt), geht es auf die eigene Seite
 * (`modules/employee/util/change-request-review-target.ts`).
 * Leitung auf dem Telefon nutzt dieselben Flächen wie am Schreibtisch: die Geräteweiche in
 * `app.component.ts` schickt sie von `/employee-mobile` ohnehin auf `/dashboard`.
 * Terminal → `/terminal-dashboard` ohne Ziel (persönliche Daten gehören nicht aufs Terminal).
 * Unbrauchbare Ids landen ohne Parameter auf derselben Fläche — ein kaputter Link öffnet die
 * Seite, nicht nichts.
 *
 * `?date=YYYY-MM-DD` am Zeitkorrektur-Kurzpfad (Tag der Stempelung in Firmenzeit) reist mit, damit
 * „Meine Zeiten“ den Tag öffnen kann; ein unlesbares Datum fällt weg (dann Liste + Hinweis).
 */

export type NotificationDeepLinkKind =
  | 'absence'
  | 'correction'
  | 'swap'
  | 'week'
  | 'openShifts'
  | 'absenceReview'
  | 'correctionReview'
  | 'changeRequest';
export type NotificationDeepLinkRole = 'terminal' | 'lead' | 'employee';

export interface NotificationDeepLinkRedirect {
  path: string;
  queryParams: Params;
}

export const TERMINAL_HOME = '/terminal-dashboard';
export const PLANNER_PATH = '/planner';
/** Flächen der Leitung für die Entscheidung über Anträge. */
export const LEAD_ABSENCE_PATH = '/absence';
export const LEAD_DASHBOARD_PATH = '/dashboard';
export const LEAD_EMPLOYEE_LIST_PATH = '/employee';

const EMPLOYEE_DESKTOP = '/employee/dashboard';
const EMPLOYEE_MOBILE = '/employee-mobile/tabs';

/** Fläche je Art: Telefon-Tab, Desktop-Bereich (gilt auch für die Leitung, außer `lead` gesetzt). */
const SURFACE: Record<NotificationDeepLinkKind, { mobile: string; desktop: string; lead?: string }> = {
  absence: { mobile: `${EMPLOYEE_MOBILE}/tab-absences`, desktop: `${EMPLOYEE_DESKTOP}/abwesenheit` },
  correction: { mobile: `${EMPLOYEE_MOBILE}/tab-time-overview`, desktop: `${EMPLOYEE_DESKTOP}/zeiten` },
  swap: { mobile: `${EMPLOYEE_MOBILE}/tab-today`, desktop: `${EMPLOYEE_DESKTOP}/heute`, lead: PLANNER_PATH },
  week: { mobile: `${EMPLOYEE_MOBILE}/tab-my-shifts`, desktop: `${EMPLOYEE_DESKTOP}/schichten`, lead: PLANNER_PATH },
  openShifts: { mobile: `${EMPLOYEE_MOBILE}/tab-today`, desktop: `${EMPLOYEE_DESKTOP}/heute`, lead: PLANNER_PATH },
  absenceReview: { mobile: `${EMPLOYEE_MOBILE}/tab-absences`, desktop: `${EMPLOYEE_DESKTOP}/abwesenheit`, lead: LEAD_ABSENCE_PATH },
  correctionReview: {
    mobile: `${EMPLOYEE_MOBILE}/tab-time-overview`,
    desktop: `${EMPLOYEE_DESKTOP}/zeiten`,
    lead: LEAD_DASHBOARD_PATH,
  },
  changeRequest: {
    mobile: '/employee-mobile/profile/change-requests',
    desktop: `${EMPLOYEE_DESKTOP}/profil/change-requests`,
    lead: LEAD_EMPLOYEE_LIST_PATH,
  },
};

/** Ziel-Parameter je Art, nachdem der Roh-Wert des Kurzpfads gelesen wurde. */
function targetParams(
  kind: NotificationDeepLinkKind,
  role: NotificationDeepLinkRole,
  raw: unknown,
  query: NotificationDeepLinkQuery,
): Params {
  switch (kind) {
    case 'absence': {
      const id = parseNotificationId(raw);
      return id ? { [NOTIFICATION_TARGET_PARAM.absence]: id } : {};
    }
    case 'correction': {
      const id = parseNotificationId(raw);
      if (!id) return {};
      const date = parseNotificationDate(query.date);
      return date
        ? { [NOTIFICATION_TARGET_PARAM.correction]: id, [NOTIFICATION_TARGET_PARAM.date]: date }
        : { [NOTIFICATION_TARGET_PARAM.correction]: id };
    }
    case 'swap': {
      const id = parseNotificationId(raw);
      if (!id) return {};
      return role === 'lead' ? { [NOTIFICATION_TARGET_PARAM.swapApprovals]: id } : { [NOTIFICATION_TARGET_PARAM.swap]: id };
    }
    case 'week': {
      if (role === 'lead') {
        const date = isoWeekMondayDate(raw);
        return date ? { date } : {};
      }
      const week = parseIsoWeek(raw);
      return week ? { [NOTIFICATION_TARGET_PARAM.week]: week } : {};
    }
    case 'openShifts':
      return {};
    case 'absenceReview': {
      const id = parseNotificationId(raw);
      if (!id) return {};
      return role === 'lead' ? { [NOTIFICATION_TARGET_PARAM.absenceReview]: id } : { [NOTIFICATION_TARGET_PARAM.absence]: id };
    }
    case 'correctionReview': {
      // Mitarbeiter: wie `correction` ohne Tag (Liste + Hinweis) — sollte nicht vorkommen.
      const id = parseNotificationId(raw);
      if (!id) return {};
      return role === 'lead'
        ? { [NOTIFICATION_TARGET_PARAM.correctionReview]: id }
        : { [NOTIFICATION_TARGET_PARAM.correction]: id };
    }
    case 'changeRequest': {
      const id = parseNotificationId(raw);
      if (!id) return {};
      return role === 'lead'
        ? { [NOTIFICATION_TARGET_PARAM.changeRequestReview]: id }
        : { [NOTIFICATION_TARGET_PARAM.changeRequest]: id };
    }
  }
}

/** Query des Kurzpfads, soweit ein Ziel sie auswertet (heute nur `date` der Zeitkorrektur). */
export interface NotificationDeepLinkQuery {
  date?: unknown;
}

export function resolveNotificationDeepLink(
  kind: NotificationDeepLinkKind,
  role: NotificationDeepLinkRole,
  raw: unknown,
  isMobile: boolean,
  query: NotificationDeepLinkQuery = {},
): NotificationDeepLinkRedirect {
  if (role === 'terminal') return { path: TERMINAL_HOME, queryParams: {} };
  const surface = SURFACE[kind];
  const path = role === 'lead' ? (surface.lead ?? surface.desktop) : isMobile ? surface.mobile : surface.desktop;
  return { path, queryParams: targetParams(kind, role, raw, query) };
}

/** Routen-Daten der Kurzpfade (`app-routing.module.ts`): `data.notificationDeepLink`. */
export const NOTIFICATION_DEEP_LINK_DATA_KEY = 'notificationDeepLink';
/** Name des Pfad-Parameters je Kurzpfad: `:id` bzw. `:isoWeek`. */
export function notificationDeepLinkParamName(kind: NotificationDeepLinkKind): string | null {
  if (kind === 'openShifts') return null;
  return kind === 'week' ? 'isoWeek' : 'id';
}

export function isNotificationDeepLinkKind(value: unknown): value is NotificationDeepLinkKind {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(SURFACE, value);
}
